import Cookies from "js-cookie";

const ACCESS_TOKEN_COOKIE = "token";
const REFRESH_TOKEN_COOKIE = "refresh_token";

/** Access token cookie lifetime (days); JWT validity is enforced server-side. */
const ACCESS_COOKIE_EXPIRES_DAYS = 1;
/** Refresh token cookie lifetime aligned with server refresh token (7 days). */
const REFRESH_COOKIE_EXPIRES_DAYS = 7;

const REFRESH_AHEAD_MS = 5 * 60 * 1000;

type TokenListener = (accessToken: string | null) => void;
type AuthFailureListener = () => void;

const cookieBaseOptions = (): Cookies.CookieAttributes => ({
  sameSite: "lax",
  secure: import.meta.env.PROD,
});

/**
 * Decodes the payload segment of a JWT without verifying the signature.
 *
 * @param token - JWT access token.
 * @returns Parsed payload or null when decoding fails.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Reads JWT `exp` claim as milliseconds since epoch.
 *
 * @param accessToken - JWT access token.
 * @returns Expiration timestamp in ms, or null when unavailable.
 */
function getAccessTokenExpirationMs(accessToken: string): number | null {
  const payload = decodeJwtPayload(accessToken);
  const exp = payload?.exp;
  if (typeof exp !== "number") {
    return null;
  }
  return exp * 1000;
}

/**
 * Centralizes access and refresh token storage for the web client.
 * Persists tokens in cookies and coordinates single-flight refresh.
 */
class AuthTokenService {
  private refreshPromise: Promise<string | null> | null = null;
  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly tokenListeners = new Set<TokenListener>();
  private readonly authFailureListeners = new Set<AuthFailureListener>();

  /**
   * Registers a listener notified when the access token changes.
   *
   * @param listener - Callback receiving the new access token or null.
   * @returns Unsubscribe function.
   */
  subscribe(listener: TokenListener): () => void {
    this.tokenListeners.add(listener);
    return () => this.tokenListeners.delete(listener);
  }

  /**
   * Registers a listener invoked when refresh fails and the session must end.
   *
   * @param listener - Callback (e.g. sign-out redirect).
   * @returns Unsubscribe function.
   */
  onAuthFailure(listener: AuthFailureListener): () => void {
    this.authFailureListeners.add(listener);
    return () => this.authFailureListeners.delete(listener);
  }

  /**
   * Returns the current access token from cookies.
   */
  getAccessToken(): string | null {
    return Cookies.get(ACCESS_TOKEN_COOKIE) ?? null;
  }

  /**
   * Returns the current refresh token from cookies.
   */
  getRefreshToken(): string | null {
    return Cookies.get(REFRESH_TOKEN_COOKIE) ?? null;
  }

  /**
   * Persists access and optional refresh tokens and notifies subscribers.
   *
   * @param accessToken - New JWT access token.
   * @param refreshToken - Optional refresh token from sign-in.
   */
  setTokens(accessToken: string, refreshToken?: string): void {
    Cookies.set(ACCESS_TOKEN_COOKIE, accessToken, {
      ...cookieBaseOptions(),
      expires: ACCESS_COOKIE_EXPIRES_DAYS,
    });

    if (refreshToken) {
      Cookies.set(REFRESH_TOKEN_COOKIE, refreshToken, {
        ...cookieBaseOptions(),
        expires: REFRESH_COOKIE_EXPIRES_DAYS,
      });
    }

    this.notifyTokenChange(accessToken);
    this.scheduleProactiveRefresh(accessToken);
  }

  /**
   * Updates only the access token (after refresh).
   *
   * @param accessToken - New JWT access token.
   */
  setAccessToken(accessToken: string): void {
    Cookies.set(ACCESS_TOKEN_COOKIE, accessToken, {
      ...cookieBaseOptions(),
      expires: ACCESS_COOKIE_EXPIRES_DAYS,
    });
    this.notifyTokenChange(accessToken);
    this.scheduleProactiveRefresh(accessToken);
  }

  /**
   * Removes all auth cookies and notifies subscribers.
   */
  clearTokens(): void {
    Cookies.remove(ACCESS_TOKEN_COOKIE);
    Cookies.remove(REFRESH_TOKEN_COOKIE);
    this.clearProactiveRefreshTimer();
    this.notifyTokenChange(null);
  }

  /**
   * Returns true when an access token exists and is not past JWT `exp`.
   */
  hasValidAccessToken(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }
    const expiresAt = getAccessTokenExpirationMs(token);
    if (!expiresAt) {
      return true;
    }
    return Date.now() < expiresAt;
  }

  /**
   * Refreshes the access token using the stored refresh token.
   * Concurrent calls share the same in-flight promise.
   *
   * @param apiBaseUrl - API root URL (e.g. websocket-server HTTP base).
   * @returns New access token or null when refresh fails.
   */
  async refreshAccessToken(apiBaseUrl: string): Promise<string | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performRefresh(apiBaseUrl).finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  /**
   * Starts listening for tab focus to refresh tokens before they expire.
   *
   * @param apiBaseUrl - API root URL.
   */
  startProactiveRefresh(apiBaseUrl: string): void {
    this.apiBaseUrl = apiBaseUrl;
    const accessToken = this.getAccessToken();
    if (accessToken) {
      this.scheduleProactiveRefresh(accessToken);
    }

    const handleVisibility = () => {
      if (document.visibilityState !== "visible" || !this.apiBaseUrl) {
        return;
      }
      const token = this.getAccessToken();
      if (!token) {
        return;
      }
      const expiresAt = getAccessTokenExpirationMs(token);
      if (
        expiresAt &&
        expiresAt - Date.now() <= REFRESH_AHEAD_MS &&
        this.getRefreshToken()
      ) {
        void this.refreshAccessToken(this.apiBaseUrl);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
  }

  private apiBaseUrl: string | null = null;

  private async performRefresh(apiBaseUrl: string): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.notifyAuthFailure();
      return null;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Type": "WEB",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        this.clearTokens();
        this.notifyAuthFailure();
        return null;
      }

      const body = (await response.json()) as { access_token?: string };
      if (!body.access_token) {
        this.clearTokens();
        this.notifyAuthFailure();
        return null;
      }

      this.setAccessToken(body.access_token);
      return body.access_token;
    } catch {
      this.clearTokens();
      this.notifyAuthFailure();
      return null;
    }
  }

  private notifyTokenChange(accessToken: string | null): void {
    for (const listener of this.tokenListeners) {
      listener(accessToken);
    }
  }

  private notifyAuthFailure(): void {
    for (const listener of this.authFailureListeners) {
      listener();
    }
  }

  private clearProactiveRefreshTimer(): void {
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  private scheduleProactiveRefresh(accessToken: string): void {
    this.clearProactiveRefreshTimer();
    const expiresAt = getAccessTokenExpirationMs(accessToken);
    if (!expiresAt || !this.getRefreshToken()) {
      return;
    }

    const delay = expiresAt - Date.now() - REFRESH_AHEAD_MS;
    if (delay <= 0) {
      return;
    }

    this.proactiveRefreshTimer = setTimeout(() => {
      this.proactiveRefreshTimer = null;
      if (this.apiBaseUrl) {
        void this.refreshAccessToken(this.apiBaseUrl);
      }
    }, delay);
  }
}

export const authTokenService = new AuthTokenService();
